// src/utils/productReplace.js

// ----- helpers -----
const toNum = (v, f = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : f;
};
const toInt = (v, f = 0) => {
  const n = Number(v);
  return Number.isInteger(n) ? n : Math.round(n || f);
};
const sum = (arr = [], fn = (x) => x) =>
  (arr || []).reduce((s, x) => s + (fn(x) || 0), 0);

// Selected sale (new UI keeps chosen sale in index 0)
const getSelectedSaleDetails = (selectedVoucherDetails) =>
  selectedVoucherDetails?.salesDetails?.[0]?.details || [];

// Old-UI-compatible sale detail finder by item
const findSaleDetailByItemId = (itemId, selectedVoucherDetails) => {
  const details = getSelectedSaleDetails(selectedVoucherDetails);
  return details.find((d) => (d.item_id) == (itemId)) || null;
};

// ----- main builder -----
export const createProductReplacePayload = (state) => {
  const {
    givenItemsList = [],
    returnItemsList = [],
    customerInfo = {},
    footerValues = {},
    voucherNumber,
    selectedTaxAcc,
    selectedVoucherDetails,
    payCart = [],
    payServiceCart: payServiceCartFromState = [],
    replaceId = 0,
  } = state;

  // prefer footer-held service cart; fallback to state-level
  const payServiceCart = footerValues?.payServiceCart?.length
    ? footerValues.payServiceCart
    : payServiceCartFromState;

  // ===== GIVEN CART =====
  const givenCart = givenItemsList
    .filter((r) => r?.item_id)
    .map((row) => {
      const item = row.fullItem || row;

      const qty = toNum(row.quantity || 0);
      const rate = toNum(row.rate ?? item.item_rate ?? item.sale_rate ?? 0);
      const sub = qty * rate;
      const taxPer = toNum(item.tax_per ?? 0);
      const tax = toNum((taxPer / 100) * sub);
      const total = toNum(sub + tax);

      return {
        warehouse_id: item.warehouse_id || 0,
        warehouse_name: item.warehouse_name ?? "",
        item_id: item.item_id,
        item_name: item.item_name ?? item.product ?? "",
        display_text: item.display_text ?? item.product ?? "",
        is_serial: item.is_serial ?? "no",

        unit_name: item.unit_name ?? "",
        unit_symbol: item.unit_symbol ?? "",
        base_unit_name: item.base_unit_name ?? null,
        conversion: toNum(item.conversion, 1),
        per_conversion: toNum(item.per_conversion ?? item.conversion, 1),
        per_unit_id: item.per_unit_id ?? item.unit_id ?? item.units?.[0]?.unit_id ?? 0,
        per_unit_symbol: item.per_unit_symbol ?? item.unit_symbol ?? "",

        qty_display: item.qty_display ?? `${qty} ${item.unit_symbol ?? ""}`.trim(),
        item_qty: qty,
        given_qty: qty,
        item_rate: rate,
        given_rate: rate,
        item_tax: tax,
        item_total: total,
        total,

        retail_qty: toNum(item.retail_qty, 0),
        item_percentage: toNum(item.item_percentage ?? 100),
        serials: item.serials ?? [],
        units: item.units ?? [],

        // keep a plain rate field (old UI style)
        rate,
      };
    });

  // ===== RETURN CART =====
  const returnCart = returnItemsList
    .filter((r) => r?.item_id)
    .map((row) => {
      const saleDetail = findSaleDetailByItemId(row.item_id, selectedVoucherDetails) || {};
      const qty = toNum(row.quantity ?? saleDetail.item_qty ?? 0);
      const rate = toNum(row.rate ?? saleDetail.item_rate ?? saleDetail.return_rate ?? 0);
      const sub = qty * rate;
      const taxPer = toNum(row.tax_per ?? row.item_tax_per ?? saleDetail.item_tax_per ?? 0);
      const tax = toNum((taxPer / 100) * sub);

      return {
        warehouse_id: row.warehouse_id ?? saleDetail.warehouse_id ?? 0,
        item_id: row.item_id,
        item_name: row.item_name ?? row.product ?? saleDetail.item_name ?? "",
        display_text: row.display_text ?? row.product ?? saleDetail.display_text ?? "",
        is_serial: row.is_serial ?? saleDetail.is_serial ?? "no",

        available_qty: toNum(row.available_qty ?? saleDetail.sold_qty ?? 0),

        return_qty: qty,
        return_rate: rate,
        return_item_qty: qty,
        return_item_rate: rate,
        return_item_total: toNum(sub),

        item_tax_per: taxPer,
        item_tax: tax,

        per_unit_id: row.per_unit_id ?? saleDetail.per_unit_id ?? row.units?.[0]?.unit_id ?? 0,
        per_unit_symbol: row.per_unit_symbol ?? saleDetail.per_unit_symbol ?? "",
        retail_qty: toNum(row.retail_qty ?? saleDetail.retail_qty ?? 0),
        return_retail_qty: toNum(row.return_retail_qty ?? qty ?? 0),
        sale_r_qty: toNum(qty),
        sale_r_rate: toNum(saleDetail.sale_rate ?? rate),

        discount_acc_id: saleDetail.discount_acc_id ?? null,
        discount_acc_name: saleDetail.discount_acc_name ?? null,
        item_discount: toNum(saleDetail.item_discount ?? 0),
        item_discount_per: toNum(saleDetail.item_discount_per ?? 0),
        tax_acc_id: saleDetail.tax_acc_id ?? null,
        tax_acc_name: saleDetail.tax_acc_name ?? null,

        purchase_average_rate: toNum(saleDetail.purchase_average_rate ?? 0, 0),
        sold_qty: toNum(saleDetail.sold_qty ?? qty ?? 0),
        serials: row.serials ?? [],
        units: row.units ?? saleDetail.units ?? [],
        base_unit_name: saleDetail.base_unit_name ?? null,
        batch_id: saleDetail.batch_id ?? null,
        conversion: toNum(row.conversion ?? saleDetail.conversion, 1),
        item_qty: toNum(row.item_qty ?? 0),
        per_conversion: toNum(saleDetail.conversion ?? 1, 1),
        per_symbol: saleDetail.per_unit_symbol ?? "",
        qty_display: `${qty || 0} ${saleDetail.per_unit_symbol ?? ""}`.trim(),
        unit_name: saleDetail.unit_name ?? "",
        unit_symbol: saleDetail.unit_symbol ?? "",
      };
    });

  // ===== Totals =====
  const givenTotal = sum(givenCart, (x) => x.item_total);
  // old UI’s return side contributed net-of-tax to amount; keep tax separate as return_total_tax
  const returnTotal = sum(returnCart, (x) => x.return_item_total);

  // Additional Expenses (MUST be computed BEFORE grandTotal)
  const othersCost =
    sum(payServiceCart, (p) => toNum(p.tran_amount)) ||
    toNum(footerValues?.othersCost ?? footerValues?.others_cost ?? 0);

  // labour (keep whatever is passed; set to 0 on Replace if unused)
  const labourCost = toNum(footerValues?.labourCost ?? footerValues?.labour_cost ?? 0);

  const subTotal = toNum(givenTotal - returnTotal);
  const grandTotal = toNum(subTotal + labourCost + othersCost);

  const paidAmount = toNum(footerValues?.paidAmount ?? 0);
  const dueAmount = toNum(grandTotal - paidAmount);

  const totalTaxGiven = sum(givenCart, (x) => toNum(x.item_tax));
  const totalTaxReturn = sum(returnCart, (x) => toNum(x.item_tax));
  const totalTax = toNum(totalTaxGiven);     // masterData.total_tax = given side tax
  const returnTotalTax = toNum(totalTaxReturn);

  // ===== masterData (old-UI compatible) =====
  const masterData = {
    voucher_no: voucherNumber ?? "",
    customer_id: customerInfo?.acc_id ?? 0,
    is_serial:
      givenCart.some((x) => x.is_serial === "yes") ||
        returnCart.some((x) => x.is_serial === "yes")
        ? "yes"
        : "no",
    narration: footerValues?.description || "",

    labour_cost: labourCost,
    others_cost: othersCost,
    total_cost: grandTotal,
    paid_amount: paidAmount,
    due_amount: dueAmount,

    replace_id: replaceId ?? 0,

    sale_id: selectedVoucherDetails?.sale_id ?? 0,
    tax_acc_id: selectedTaxAcc?.acc_id ??
      getSelectedSaleDetails(selectedVoucherDetails)?.[0]?.tax_acc_id ??0,

    total_tax: totalTax,
    return_total_tax: returnTotalTax,
  };

  return { givenCart, returnCart, masterData, payCart, payServiceCart };
};
